import { apiClient } from '../../services/apiClient';
import type {
  UsersResponse,
  CreateInvitationRequest,
  CreateInvitationResponse,
  RegisteredUser,
} from './types';

/**
 * ログイン中ユーザー自身のプロフィールを取得する。
 * 返却形は用途によって参照するキーが異なるため、呼び出し側で必要な型を当てる。
 */
export async function getCurrentUser<T = any>(): Promise<T> {
  const { data } = await apiClient.get<T>('/users/me');
  return data;
}

/**
 * ユーザー一覧を取得（管理者のみ）
 */
export async function listUsers(): Promise<UsersResponse> {
  const { data } = await apiClient.get<UsersResponse>('/users');
  return data;
}

/**
 * ユーザーを招待（管理者のみ）
 */
export async function inviteUsers(
  request: CreateInvitationRequest
): Promise<CreateInvitationResponse> {
  const { data } = await apiClient.post<CreateInvitationResponse>(
    '/invitations',
    request
  );
  return data;
}

/**
 * 招待を再送信（管理者のみ）
 */
export async function resendInvitation(invitationId: string): Promise<void> {
  await apiClient.post(`/invitations/${invitationId}/resend`);
}

/**
 * 招待を取り消し（管理者のみ）
 */
export async function cancelInvitation(invitationId: string): Promise<void> {
  await apiClient.delete(`/invitations/${invitationId}`);
}

/**
 * ユーザー情報を更新（管理者のみ）
 */
export async function updateUser(
  userId: string,
  data: { display_name?: string; picture_url?: string }
): Promise<{ success: boolean; user: RegisteredUser }> {
  const response = await apiClient.patch<{ success: boolean; user: RegisteredUser }>(
    `/users/${userId}`,
    data
  );
  return response.data;
}

/**
 * ユーザーのステータスを変更（管理者のみ）
 */
export async function updateUserStatus(
  userId: string,
  isActive: boolean
): Promise<{ success: boolean; user: RegisteredUser }> {
  const response = await apiClient.patch<{ success: boolean; user: RegisteredUser }>(
    `/users/${userId}/status`,
    { is_active: isActive }
  );
  return response.data;
}

/**
 * ユーザーのロールを変更（管理者のみ）
 */
export async function updateUserRole(
  userId: string,
  role: 'admin' | 'user'
): Promise<{ message: string; user: RegisteredUser }> {
  const response = await apiClient.patch<{ message: string; user: RegisteredUser }>(
    `/users/${userId}/role`,
    { role }
  );
  return response.data;
}

/**
 * ユーザーを削除（管理者のみ）
 */
export async function deleteUser(userId: string): Promise<void> {
  await apiClient.delete(`/users/${userId}`);
}
